import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { LngLat, WunderMapInterface } from '../types';
import {
  Loader,
  Paragraph,
  WunderMap,
  MapRef,
  MapProps,
  LngLatBoundsLike,
} from '@wunder-mobility/ui-components';
import wunderMap from '../assets/scss/WunderMap.module.scss';
import { Trans } from '@lingui/react/macro';
import { useTypedDispatch, useTypedSelector } from '@/core/redux/hooks';
import { setUserLocationCoordinates } from '@/features/account/redux/account.reducer';
import { getUserLocationCoordinates } from '@/features/account/redux/account.selectors';
import { useRequestMapSettingsQuery } from '@/core/services/SettingsManagement.service';

type WunderMapUserLocationPermissionStates = 'uninitialized' | 'denied' | 'approved' | 'requested';

type MapInitialViewState = NonNullable<MapProps['initialViewState']>;

// Last-resort framing when we know neither the user's position nor the tenant's
// configured centre: fit as much of continental Europe as the viewport allows.
const EUROPE_BOUNDS: LngLatBoundsLike = [
  [-10.5, 34.5],
  [31.6, 71.2],
];

const DEFAULT_ZOOM = 11;
const USER_LOCATION_ZOOM = 14;
const DEFAULT_MIN_ZOOM = 9;
// Europe does not fit at DEFAULT_MIN_ZOOM, so the bounds fallback needs its own floor.
const EUROPE_MIN_ZOOM = 3;

// When initially opening the app, position the map accordingly
const urlSearchParams = new URLSearchParams(window.location.search);

const [urlLng, urlLat] =
  urlSearchParams
    .get('c')
    ?.split(',')
    ?.map((cord) => Number(cord)) ?? [];

const urlCoordinates: LngLat | null =
  Number.isFinite(urlLng) && Number.isFinite(urlLat) ? { lat: urlLat, lng: urlLng } : null;

const urlZoom = Number(urlSearchParams.get('z')) || undefined;

const WunderMaps: React.FC<PropsWithChildren<WunderMapInterface>> = ({ children, onMapLoaded }) => {
  const userCoordinates = useTypedSelector(getUserLocationCoordinates);
  const mapRef = useRef<MapRef | null>(null);
  const [userLocationPermissions, setUserLocationPermissions] =
    useState<WunderMapUserLocationPermissionStates>('uninitialized');
  const dispatch = useTypedDispatch();

  const { data: mapSettings, isLoading: isLoadingMapSettings } = useRequestMapSettingsQuery();
  const { initialLatitude, initialLongitude, mapDefaultZoomLvl } = mapSettings?.value ?? {};

  // The tenant's configured centre, when it has one set.
  const fallbackCenter: LngLat | null =
    Number.isFinite(initialLatitude) && Number.isFinite(initialLongitude)
      ? { lat: initialLatitude!, lng: initialLongitude! }
      : null;

  const hasRequestedLocationRef = useRef(false);

  useEffect(() => {
    // Wait for the tenant's map settings, otherwise a denial that comes back before
    // they land would skip the configured centre and fall through to Europe.
    if (isLoadingMapSettings || hasRequestedLocationRef.current) {
      return;
    }

    hasRequestedLocationRef.current = true;

    const denyAndFallBack = () => {
      if (fallbackCenter) {
        dispatch(setUserLocationCoordinates(fallbackCenter));
      }

      setUserLocationPermissions('denied');
    };

    if (userCoordinates) {
      setUserLocationPermissions('approved');
    } else if (!navigator?.geolocation) {
      denyAndFallBack();
    } else {
      setUserLocationPermissions('requested');

      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          dispatch(setUserLocationCoordinates({ lat: coords.latitude, lng: coords.longitude }));
          setUserLocationPermissions('approved');
        },
        denyAndFallBack,
        {
          enableHighAccuracy: false,
          timeout: 6000,
        },
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isLoadingMapSettings]);

  // Positioning priority: the `c` URL param, then the user's own position (which the
  // denied path backfills with the tenant centre), then whatever Europe we can fit.
  const initialViewState = useMemo<MapInitialViewState>(() => {
    if (urlCoordinates) {
      return {
        longitude: urlCoordinates.lng,
        latitude: urlCoordinates.lat,
        zoom: urlZoom ?? mapDefaultZoomLvl ?? DEFAULT_ZOOM,
      };
    }

    if (userCoordinates) {
      return {
        longitude: userCoordinates.lng,
        latitude: userCoordinates.lat,
        zoom: USER_LOCATION_ZOOM,
      };
    }

    return { bounds: EUROPE_BOUNDS, fitBoundsOptions: { padding: 24 } };
  }, [userCoordinates, mapDefaultZoomLvl]);

  if (userLocationPermissions === 'uninitialized') {
    return null;
  }

  if (userLocationPermissions === 'requested') {
    return (
      <Loader fixed cover>
        <Paragraph weight="medium">
          <Trans id="geomap.goMap.waitingPermissions">Waiting for location information...</Trans>
        </Paragraph>
      </Loader>
    );
  }

  return (
    <div data-testid="wunderMap" className={wunderMap['wunder-map']}>
      <WunderMap
        maxZoom={20}
        minZoom={'bounds' in initialViewState ? EUROPE_MIN_ZOOM : DEFAULT_MIN_ZOOM}
        ref={mapRef}
        onMapLoaded={onMapLoaded}
        initialViewState={initialViewState}
        mapStyle="mapbox://styles/mapbox/streets-v12?optimize=true"
      >
        {children}
      </WunderMap>
    </div>
  );
};

export default WunderMaps;
